/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React from 'react';

import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';

import type {
	ObjectAddCommentProfileWrapperUserQuery as ObjectAddCommentProfileWrapperUserQuery$data,
	ObjectAddCommentProfileWrapperUserQueryVariables as ObjectAddCommentProfileWrapperUserQuery$variables,
} from './__types__/ObjectAddCommentProfileWrapperUserQuery';

const ObjectAddCommentProfileWrapperUserQuery = gql`
	query ObjectAddCommentProfileWrapperUserQuery($id: ID!) {
		user: confluence_atlassianUser(id: $id) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;

const styles = cssMap({
	addCommentOpenContainerStyles: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		gap: token('space.200'),
		alignItems: 'flex-start',
	},
	addCommentEditorStyles: {
		width: '100%',
	},
	avatarStyles: {
		width: '32px',
		height: '32px',
	},
});

export interface ObjectAddCommentProfileWrapperProps {
	children: React.ReactNode;
}

export const ObjectAddCommentProfileWrapper = ({
	children,
}: ObjectAddCommentProfileWrapperProps) => {
	const { userId } = useSessionData();
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const { data: userData, loading } = useQueryInterop<
		ObjectAddCommentProfileWrapperUserQuery$data,
		ObjectAddCommentProfileWrapperUserQuery$variables
	>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		ObjectAddCommentProfileWrapperUserQuery,
		{
			variables: { id: userId || '' },
			fetchPolicy: 'cache-and-network',
			// Skip the query if data is provided or the user is anonymous
			skip: !userId,
		},
	);

	const canViewProfile = true;
	const userInfo = {
		avatarUrl: userData?.user?.photos?.[0]?.value || '',
		accountId: userId,
		displayName: userData?.user?.displayName || '',
	};

	return (
		<Box xcss={styles.addCommentOpenContainerStyles}>
			<Box
				xcss={styles.avatarStyles}
				style={
					// float left not possible in map
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					{ float: 'left' }
				}
			>
				{loading || renderServerPlaceholder ? null : (
					<ProfileAvatar
						canViewProfile={canViewProfile}
						src={userInfo.avatarUrl}
						userId={userInfo.accountId}
						isAnonymous={!userInfo.accountId}
						name={userInfo.displayName}
						testId="profile-avatar-comment"
					/>
				)}
			</Box>
			<Box xcss={styles.addCommentEditorStyles}>{children}</Box>
		</Box>
	);
};
